/**
 * 平台账户页面 — PlatformAccount（二期批3 翻新，对齐概念稿 platform-account.html）。
 *
 * GET /api/accounts 获取平台用户账户列表（租户实体）。
 * admin 看全部用户账户，user 只看自己；页面为 admin 专属（菜单批1已过滤）。
 *
 * 结构：4 指标卡（账户总数/运营方/企业客户/状态正常）→ 账户表 → 口径说明卡。
 *
 * 保留批 1 工作：account_type 三值枚举标签（operator/enterprise_client/platform，
 * 历史值 geo_service_provider 兜底展示）。
 *
 * R3 说明：本页数据源为租户账户（accounts 表，登录主体），无 company/brand/product
 * 归属列，非 PRD §4.6.2 资源池语义，故不加共享/专属徽标；R3 资源表
 * （platform_accounts）由「媒体账户」页承接。
 *
 * 概念稿裁剪：AI 平台探测账户数/日探测额度/已用额度/额度使用分布/最近探测时间
 * 无后端数据源（llm_call_log 无按平台配额聚合端点），按"接口没有不虚构"原则跳过；
 * 探测通道配置在「系统设置」页（LLM API Key + 监测平台清单）。
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getAccounts, updateAccountLimits } from "../api/accounts";
import { useAuthStore } from "../store/authStore";
import { formatDate } from "../utils/format";
import type { Account } from "../types";

/** 账户状态映射。 */
const ACCOUNT_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: "正常", color: "success.main", bg: "semanticBg.success" },
  suspended: { label: "已暂停", color: "error.main", bg: "semanticBg.error" },
  initial_setup: { label: "待初始化", color: "warning.main", bg: "semanticBg.warning" },
};

export default function PlatformAccount() {
  const [items, setItems] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 配额编辑对话框状态（仅 admin 可见入口；权限最终由后端拦截）
  const { account } = useAuthStore();
  const isAdmin = account?.role === "admin";
  const [editTarget, setEditTarget] = useState<Account | null>(null);
  const [editCompanies, setEditCompanies] = useState("");
  const [editProducts, setEditProducts] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAccounts();
      setItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取平台账户失败");
    } finally {
      setLoading(false);
    }
  };

  /** 打开配额编辑对话框。 */
  const openEdit = (row: Account) => {
    setEditTarget(row);
    setEditCompanies(String(row.max_companies ?? 0));
    setEditProducts(String(row.max_products ?? 0));
    setSaveError("");
  };

  /** 提交配额调整。 */
  const handleSave = async () => {
    if (!editTarget) return;
    const maxCompanies = Number(editCompanies);
    const maxProducts = Number(editProducts);
    if (
      !Number.isInteger(maxCompanies) || maxCompanies < 0 ||
      !Number.isInteger(maxProducts) || maxProducts < 0
    ) {
      setSaveError("配额必须为非负整数");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const result = await updateAccountLimits(editTarget.id, {
        max_companies: maxCompanies,
        max_products: maxProducts,
      });
      setEditTarget(null);
      setToast(
        `已更新「${result.account_name}」配额：公司 ${result.max_companies} / 产品 ${result.max_products}`,
      );
      fetchAccounts();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "配额更新失败");
    } finally {
      setSaving(false);
    }
  };

  /** 聚合指标（租户类型/状态）。 */
  const stats = useMemo(() => {
    const operators = items.filter((a) => a.account_type === "operator").length;
    const clients = items.filter((a) => a.account_type === "enterprise_client").length;
    const active = items.filter((a) => a.status === "active").length;
    return { total: items.length, operators, clients, active };
  }, [items]);

  const columns: DataTableColumn<Account>[] = [
    {
      key: "name",
      label: "账户名称",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
          {row.account_name}
        </Typography>
      ),
    },
    {
      key: "email",
      label: "邮箱",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.email}
        </Typography>
      ),
    },
    {
      key: "role",
      label: "角色",
      align: "center",
      render: (row) => (
        <Badge
          label={row.role === "admin" ? "管理员" : "客户"}
          variant={row.role === "admin" ? "info" : "default"}
        />
      ),
    },
    {
      key: "type",
      label: "账户类型",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {/* 二期批1 对齐三值枚举；geo_service_provider 为存量历史值保留展示兜底 */}
          {row.account_type === "operator" ? "运营方" :
           row.account_type === "enterprise_client" ? "企业客户" :
           row.account_type === "platform" ? "平台" :
           row.account_type === "geo_service_provider" ? "GEO 服务商" :
           row.account_type}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => {
        const info = ACCOUNT_STATUS_MAP[row.status] || {
          label: row.status,
          color: "text.secondary",
          bg: "background.default",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
    {
      key: "limits",
      label: "配额（公司/产品）",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.max_companies || "—"} / {row.max_products || "—"}
        </Typography>
      ),
    },
    {
      key: "created",
      label: "创建时间",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.created_at)}
        </Typography>
      ),
    },
  ];

  // 配额编辑列仅 admin 可见（页面本身 admin 专属，此处为双保险）
  if (isAdmin) {
    columns.push({
      key: "actions",
      label: "操作",
      align: "center",
      render: (row) => (
        <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
          编辑配额
        </Button>
      ),
    });
  }

  return (
    <Box>
      <PageHeader
        title="平台账户"
        breadcrumbs={[{ label: "系统" }, { label: "平台账户" }]}
        subtitle="FHSAI GEO 智能增长平台租户账户 · 开户走 skill 编排（低频 + 人工审核），权限判断在后端"
        actions={
          <Badge label="admin 专属页" variant="info" size="medium" />
        }
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="账户总数"
            value={loading ? "—" : stats.total}
            subtitle="平台租户实体（登录主体）"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="运营方"
            value={loading ? "—" : stats.operators}
            subtitle="account_type = operator · 二期主形态"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="企业客户"
            value={loading ? "—" : stats.clients}
            subtitle="account_type = enterprise_client"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="状态正常"
            value={loading ? "—" : stats.active}
            subtitle={
              stats.total > 0
                ? `正常率 ${((stats.active / stats.total) * 100).toFixed(0)}%`
                : undefined
            }
          />
        </Grid>
      </Grid>

      {/* 账户表 */}
      {loading ? (
        <Loading fullPage />
      ) : error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText="暂无账户数据"
        />
      )}

      {/* 口径说明 */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            权限层级（PRD §4.6.5）：平台超管（admin）→ 运营方（管理其名下公司）→
            公司 = 数据、非登录主体，不设"公司用户"层。配额列 = 单账户可建
            公司/产品上限（max_companies / max_products），创建时后端拦截。
            admin 可在本页直接编辑配额（调低不回收存量，仅拦截后续创建）。
            运营方开户由 skill 编排收集资料并调后端开户 API。
            AI 平台探测通道（豆包 / DeepSeek / 通义千问 / 元宝 / 文心一言）的
            Key 与平台清单配置见「系统设置」页。
          </Typography>
        </CardContent>
      </Card>

      {/* 配额编辑对话框（仅 admin 入口） */}
      <Dialog
        open={editTarget !== null}
        onClose={() => !saving && setEditTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16 }}>
          编辑配额 — {editTarget?.account_name}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField
            label="公司配额（max_companies）"
            type="number"
            size="small"
            value={editCompanies}
            onChange={(e) => setEditCompanies(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
          <TextField
            label="产品配额（max_products）"
            type="number"
            size="small"
            value={editProducts}
            onChange={(e) => setEditProducts(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            非负整数。调低到低于当前已用量不会回收存量，仅拦截后续创建。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={saving}>
            取消
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "保存中…" : "保存"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={toast !== ""}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
      />
    </Box>
  );
}
